import { useEffect, useState } from 'react';
import { useAuthStore } from '@/shared/store/auth';
import { cn } from '@/shared/lib/cn';

interface Props {
  /** Endpoint protegido que devuelve la imagen (auth Bearer + cookie). */
  url: string;
  alt: string;
  className?: string;
  /** Qué mostrar si la imagen no se puede cargar (404, sin firma, etc.). */
  fallback?: JSX.Element;
}

/**
 * Muestra una imagen servida por un endpoint protegido con JWT. Como `<img>`
 * no puede enviar headers de auth, hacemos `fetch` con `Authorization: Bearer`
 * y convertimos la respuesta en `blob:` URL. Limpia el ObjectURL al desmontar.
 */
export function AuthenticatedImage({ url, alt, className, fallback }: Props): JSX.Element {
  const accessToken = useAuthStore((s) => s.accessToken);
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    let created: string | null = null;
    setError(false);
    setSrc(null);

    const run = async (): Promise<void> => {
      try {
        const res = await fetch(url, {
          headers:
            accessToken !== null && accessToken.length > 0
              ? { Authorization: `Bearer ${accessToken}` }
              : {},
          credentials: 'include',
        });
        if (!res.ok) throw new Error(String(res.status));
        const blob = await res.blob();
        if (cancelled) return;
        created = URL.createObjectURL(blob);
        setSrc(created);
      } catch {
        if (!cancelled) setError(true);
      }
    };

    void run();
    return () => {
      cancelled = true;
      if (created !== null) URL.revokeObjectURL(created);
    };
  }, [url, accessToken]);

  if (error) return fallback ?? <span className="text-[11px] text-text-muted">Sin firma</span>;
  if (src === null) return <div className={cn('h-16 w-full animate-pulse rounded bg-surface-alt')} />;
  return <img src={src} alt={alt} className={className} />;
}
